import { toneSoft, type PwaTone } from '@/components/pwa/tone'
import type { DeliveryState } from '@/types/logistics'

export const BackendDeliveryState = {
  AwaitingPickup: 'awaiting_pickup',
  PickupReady: 'pickup_ready',
  Created: 'created',
  EnRoute: 'en_route',
  Dropped: 'dropped',
  Received: 'received',
  OutForDelivery: 'out_for_delivery',
  Delivered: 'delivered',
  Failed: 'failed',
  ReturnRequested: 'return_requested',
  ReturnDeposited: 'return_deposited',
  ReturnCollected: 'return_collected',
  Disputed: 'disputed',
  Cancelled: 'cancelled'
} as const

export const UiDeliveryState = {
  AwaitingPickup: 'À RETRAIR',
  PickupReady: 'PRÊT À REMETTRE',
  Created: 'CRÉÉ',
  EnRoute: 'EN_ROUTE',
  Dropped: 'DÉPOSÉ',
  Received: 'REÇU',
  OutForDelivery: 'EN_COURS',
  Delivered: 'LIVRÉ',
  Failed: 'ÉCHOUÉ',
  ReturnRequested: 'RETOUR_DEMANDÉ',
  ReturnDeposited: 'RETOUR_DÉPOSÉ',
  ReturnCollected: 'RETOUR_RÉCUPÉRÉ',
  Disputed: 'LITIGIEUX',
  Cancelled: 'ANNULÉ'
} as const satisfies Record<string, DeliveryState>

/**
 * The backend value for a parcel's UI state. A `PackageSummary` carries the UI label (`REÇU`), while
 * buckets, filters and the API speak the backend value (`received`); comparing one with the other
 * never matches. Both tables share their keys, so the conversion is by key.
 */
export function backendStateOf(uiState: string | null | undefined): string | undefined {
  const key = (Object.keys(UiDeliveryState) as Array<keyof typeof UiDeliveryState>).find(
    (candidate) => UiDeliveryState[candidate] === uiState
  )

  return key ? BackendDeliveryState[key] : undefined
}

export const DropSessionStatus = {
  Dropped: 'dropped',
  Received: 'received'
} as const

export const HandoverFilterStatus = {
  All: 'all',
  Pending: 'pending',
  Partial: 'partial',
  Received: 'received'
} as const

export const PartnerStatus = {
  Active: 'active',
  Suspended: 'suspended',
  Closed: 'closed'
} as const

export type PartnerStatusValue = (typeof PartnerStatus)[keyof typeof PartnerStatus]

export const PartnerCreditRequestStatus = {
  Pending: 'pending',
  Approved: 'approved',
  Rejected: 'rejected'
} as const

export type PartnerCreditRequestStatusValue = (typeof PartnerCreditRequestStatus)[keyof typeof PartnerCreditRequestStatus]

export const PartnerCreditEntryType = {
  CreditTopup: 'credit_topup',
  PartnerFeeDebit: 'partner_fee_debit',
  CreditAdjustment: 'credit_adjustment'
} as const

export type PartnerCreditEntryTypeValue = (typeof PartnerCreditEntryType)[keyof typeof PartnerCreditEntryType]

/**
 * How a delivery state reads and looks, in one place.
 *
 * `StatusBadge` owned this map privately, so a surface that needed the word for a state wrote its own
 * or reached for the backend's copy — which is how the partner's monthly report ended up printing the
 * raw `out_for_delivery` at a partner, and how the resident return list fed a French label into a
 * component that expects a state value. The badge keeps the icons (presentation); the wording key and
 * the colours are vocabulary and live here.
 *
 * `Failed` used the brand colour, so a failure looked like an ordinary brand event next to `Disputed`,
 * which is red. A failed delivery gets the destructive token like every other refusal in the app.
 */
export const DELIVERY_STATE_META: Record<DeliveryState, { labelKey: string; className: string; tone: PwaTone }> = {
  [UiDeliveryState.Created]: { labelKey: 'state.created', className: toneSoft.neutral, tone: 'neutral' },
  [UiDeliveryState.AwaitingPickup]: { labelKey: 'state.awaiting_pickup', className: toneSoft.warning, tone: 'warning' },
  [UiDeliveryState.PickupReady]: { labelKey: 'state.pickup_ready', className: toneSoft.info, tone: 'info' },
  [UiDeliveryState.EnRoute]: { labelKey: 'state.en_route', className: toneSoft.info, tone: 'info' },
  [UiDeliveryState.Dropped]: { labelKey: 'state.dropped', className: toneSoft.info, tone: 'info' },
  [UiDeliveryState.Received]: { labelKey: 'state.received', className: toneSoft.info, tone: 'info' },
  [UiDeliveryState.OutForDelivery]: { labelKey: 'state.out_for_delivery', className: toneSoft.warning, tone: 'warning' },
  [UiDeliveryState.Delivered]: { labelKey: 'state.delivered', className: toneSoft.success, tone: 'success' },
  [UiDeliveryState.Failed]: { labelKey: 'state.failed', className: toneSoft.primary, tone: 'primary' },
  [UiDeliveryState.ReturnRequested]: { labelKey: 'state.return_requested', className: toneSoft.warning, tone: 'warning' },
  [UiDeliveryState.ReturnDeposited]: { labelKey: 'state.return_deposited', className: toneSoft.info, tone: 'info' },
  [UiDeliveryState.ReturnCollected]: { labelKey: 'state.return_collected', className: toneSoft.success, tone: 'success' },
  [UiDeliveryState.Disputed]: { labelKey: 'state.disputed', className: toneSoft.primary, tone: 'primary' },
  [UiDeliveryState.Cancelled]: { labelKey: 'state.cancelled', className: toneSoft.neutral, tone: 'neutral' }
}

/**
 * The job colour of a state, for a surface that needs more than a badge's classes — a timeline dot,
 * a tinted card, an icon tile. An unknown state is neutral, never a thrown render.
 */
export function deliveryStateTone(state: string | null | undefined): PwaTone {
  const meta = state ? DELIVERY_STATE_META[state as DeliveryState] : undefined

  return meta?.tone ?? 'neutral'
}

/**
 * The word for a delivery state, translated. Returns the raw value when the state is one the catalogue
 * does not know, so an unknown state reads as `return_requested` rather than as a blank badge or a
 * thrown render.
 */
export function deliveryStateLabel(state: string | null | undefined, t: (key: string) => string): string {
  if (!state) return ''

  const meta = DELIVERY_STATE_META[state as DeliveryState]
  if (!meta) return String(state)

  const label = t(meta.labelKey)

  return label === meta.labelKey ? String(state) : label
}

/** The badge colours for a delivery state, tolerant of a state we do not know. */
export function deliveryStateClass(state: string | null | undefined): string {
  const meta = state ? DELIVERY_STATE_META[state as DeliveryState] : undefined

  return meta?.className ?? toneSoft.neutral
}

/**
 * Badge colours and wording for the states that appear on several surfaces.
 *
 * These were copy-pasted: account state into three admin pages, credit-request state into three more,
 * plus a fourth copy on the resident page that used raw Tailwind palette colours
 * (`bg-emerald-500/10 text-emerald-700 …`) instead of the design tokens — so the same approved
 * request was green-grey on one screen and a different green on another, and it was the only place in
 * the app still hardcoding a palette. One concept, one function, tokens only.
 */

/** Account state (active / suspended / disabled) — the admin lists. */
export function accountStatusClass(status: string): string {
  if (status === 'active') return 'bg-success/10 text-success border-success/20'
  if (status === 'suspended') return 'bg-warning/15 text-warning border-warning/30'
  if (status === 'disabled') return 'bg-destructive/10 text-destructive border-destructive/20'

  return 'bg-info/10 text-info border-info/20'
}

/**
 * The wording for an account state; the page translates it and falls back to the raw value. A missing
 * state reads as empty — an absent status is not the word "undefined".
 */
export function accountStatusLabel(status: string | null | undefined, t: (key: string) => string): string {
  if (!status) return ''

  const key = `adminLists.statuses.${status}`
  const label = t(key)

  return label === key ? status : label
}

/**
 * Credit / order request state. `converted` is the resident-facing extra state and shares the "done"
 * colour with `approved`, so a resident and an agent looking at the same request see the same green.
 */
export function creditRequestStatusClass(status: string): string {
  if (status === PartnerCreditRequestStatus.Approved || status === 'approved' || status === 'converted') {
    return 'bg-success/10 text-success border-success/20'
  }
  if (status === PartnerCreditRequestStatus.Rejected || status === 'rejected') {
    return 'bg-destructive/10 text-destructive border-destructive/20'
  }
  if (status === PartnerCreditRequestStatus.Pending || status === 'pending') {
    return 'bg-warning/10 text-warning border-warning/20'
  }

  return 'bg-muted text-muted-foreground border-border'
}
